import Hamster, { Adapter, Transform } from 'hamster-redux';
import moment from 'moment';

import { makeQueryConfig } from 'cityads-v3/utils/apiUtils';
import config from 'cityads-v3/config';

const SADUKA_CHUNK_SIZE = 20;

export class SadukaAdapter extends Adapter {
  getChunkSize() {
    return SADUKA_CHUNK_SIZE;
  }

  getHost() {
    return config.SADUKA_URL;
  }

  getNamespace() {
    return 'saduka';
  }

  getFetchOptions(url, method, params, options = {}) {
    const fetchOptions = super.getFetchOptions(url, method, params, options);

    const reduxStore = this._container.get('redux');

    return makeQueryConfig(url, fetchOptions, reduxStore.getState);
  }

  updateEntity(typeName, id, data, options) {
    const url = this.buildUrl(typeName, id);

    return this.request(url, 'PATCH', { body: data }, options);
  }
}

export class DateTransform extends Transform {
  unserialize(serialized) {
    const type = typeof serialized;

    switch (type) {
      case 'string':
      case 'number': {
        const parsed = new Date(serialized);

        if (!isNaN(parsed)) {
          return parsed;
        }

        return moment(serialized).toDate();
      }
      case 'undefined':
        return serialized;
      default:
        return null;
    }
  }

  serialize(unserialized) {
    if (unserialized instanceof Date && !isNaN(unserialized)) {
      return unserialized.toISOString();
    } else {
      return null;
    }
  }
}

export const ENTITY_DESCRIPTIONS = {
  geo: {
    schema: {
      id: 'number',
      name: 'string',
    },
  },

  offer: {
    schema: {
      id: 'number',
      name: 'string',
      shop_id: 'string',
      commission_data: 'object',
    },
  },

  offerTarget: {
    schema: {
      id: 'string',
      text: 'string',
      offer: 'number',
      target_url: 'string',
      webmaster_commission: 'number',
      epa_percent: 'number',
      per_sales_show: 'number',
      per_sales_show_max: 'number',
      currency_id: 'number',
      sort: 'number',
      show_percent: 'boolean',
    },
  },

  googleAccount: {
    schema: {
      id: 'number',
      user_id: 'number',
      is_active: 'boolean',
      name: 'string',
      email: 'string',
      picture_url: 'string',
      updated_at: 'date',
    },
  },

  googleAdsAccount: {
    schema: {
      id: 'number',
      google_account_id: 'number',
      currency_id: 'number',
      client_customer_id: 'string',
      is_active: 'boolean',
      updated_at: 'date',
    },
  },

  googleAdsIntegration: {
    schema: {
      id: 'number',
      google_ads_account_id: 'number',
      offer_id: 'number',
      user_id: 'number',
      is_active: 'boolean',
      created_at: 'date',
    },
  },

  microsoftAccount: {
    schema: {
      id: 'number',
      user_id: 'number',
      is_active: 'boolean',
      name: 'string',
      login: 'string',
      picture_url: 'string',
      updated_at: 'date',
    },
  },

  microsoftAdsAccount: {
    schema: {
      id: 'number',
      microsoft_account_id: 'number',
      currency_id: 'number',
      customer_number: 'string',
      is_active: 'boolean',
      updated_at: 'date',
    },
  },

  microsoftAdsCampaign: {
    schema: {
      id: 'number',
      user_id: 'number',
      microsoft_ads_account_id: 'number',
      name: 'string',
      budget_type: 'number',
      daily_budget: 'number',
      customer_id: 'number',
      is_active: 'boolean',
      updated_at: 'date',
    },
  },

  microsoftAdsIntegration: {
    schema: {
      id: 'number',
      microsoft_ads_account_id: 'number',
      microsoft_ads_campaign_id: 'number',
      offer_id: 'number',
      user_id: 'number',
      is_active: 'boolean',
      created_at: 'date',
    },
  },

  facebookAccount: {
    schema: {
      id: 'number',
      user_id: 'number',
      is_active: 'boolean',
      name: 'string',
      email: 'string',
      picture_url: 'string',
      updated_at: 'date',
      authorization_expires_at: 'date',
    },
  },

  facebookAdsAccount: {
    schema: {
      id: 'number',
      facebook_account_id: 'number',
      currency_id: 'number',
      external_account_id: 'string',
      is_active: 'boolean',
      updated_at: 'date',
    },
  },

  facebookAdsIntegration: {
    schema: {
      id: 'number',
      facebook_ads_account_id: 'number',
      offer_id: 'number',
      user_id: 'number',
      is_active: 'boolean',
      created_at: 'date',
    },
  },

  parkedDomain: {
    schema: {
      id: 'number',
      user_id: 'number',
      status_id: 'number',
      name: 'string',
      comment: 'string',
      created_at: 'date',
    },
  },

  paymentPlanType: {
    schema: {
      id: 'string',
      code: 'string',
      name: 'string',
    },
  },

  externalAccount: {
    schema: {
      id: 'string',
      name: 'string',
      is_primary: 'boolean',
      payment_plan_type: 'string',
      limit_amount_min_once: 'number',
      currency_code: 'string',
      is_autopayable: 'boolean',
    },
  },

  request: {
    schema: {
      id: 'string',
      date: 'date',
      status_code: 'string',
      update_reason: 'string',
      amount_writeoff_to: 'number',
      amount_to: 'number',
      tax_compensation_amount_to: 'number',
      tax_hold_amount_to: 'number',
      currency_to: 'string',
      account_to_number: 'string',
      user_currency_total_amount: 'number',
      user_currency_code: 'string',
    },
  },

  requestStatus: {
    schema: {
      id: 'string',
      name: 'string',
    },
  },
};

export default function configureHamster({ store }) {
  const hamster = new Hamster();

  hamster.registerRedux(store);
  hamster.registerReducer('hamster');

  hamster.registerFactory('adapter:default', SadukaAdapter);

  hamster.registerFieldType('date', { transform: DateTransform });

  Object.entries(ENTITY_DESCRIPTIONS).forEach(([entityName, entityDescription]) => {
    hamster.registerEntityType(entityName, entityDescription);
  });

  return hamster;
}
